/**
 * Detection helpers for the Bhookly Server Android WebView shell.
 * The native app injects `window.BhooklyNative` with printJobs().
 *
 * Android WebView quirks:
 * - Interface methods often fail `typeof === 'function'` checks
 * - Reading undeclared properties on the bridge can throw
 * Prefer `window.BhooklyNativeApp` (set by the shell) + callable printJobs.
 */

export interface BhooklyNativeBridge {
  isNativeServerApp?: boolean
  getIsNativeServerApp?: () => boolean
  printJobs?: (jobsJson: string) => void
  getTenantHost?: () => string
  /** Push receipt logo + business name into the Android chrome / task icon. */
  setBranding?: (logoUrl: string, businessName: string) => void
}

declare global {
  interface Window {
    BhooklyNative?: BhooklyNativeBridge
    BhooklyNativeApp?: boolean
  }
}

/** True when running inside the Bhookly Server Android WebView. */
export function isBhooklyNativeServerApp(): boolean {
  if (typeof window === 'undefined') return false
  // Shell marker — most reliable (set on every page load / SPA nav).
  if (window.BhooklyNativeApp === true) return true

  const n = window.BhooklyNative
  if (!n) return false

  try {
    if (n.isNativeServerApp === true) return true
  } catch {
    /* Android may throw on property get */
  }
  try {
    if (n.getIsNativeServerApp?.()) return true
  } catch {
    /* bridge not ready */
  }
  // Any injectable print entrypoint means we're in the shell.
  try {
    if (n.printJobs != null) return true
  } catch {
    /* ignore */
  }
  return false
}

/** Immersive server tablet mode (?embedded=server or native shell). */
export function isEmbeddedServerMode(): boolean {
  if (typeof window === 'undefined') return false
  if (isBhooklyNativeServerApp()) return true
  try {
    return new URLSearchParams(window.location.search).get('embedded') === 'server'
  } catch {
    return false
  }
}

export function nativePrintJobs(jobsJson: string): boolean {
  const n = window.BhooklyNative
  if (!n) return false
  try {
    // Do NOT use typeof === 'function' — Android JavascriptInterface methods
    // often fail that check even when callable.
    if (n.printJobs == null) return false
    n.printJobs(jobsJson)
    return true
  } catch {
    return false
  }
}

export function nativeSetBranding(logoUrl: string, businessName: string): void {
  if (!isBhooklyNativeServerApp()) return
  try {
    window.BhooklyNative?.setBranding?.(logoUrl || '', businessName || '')
  } catch {
    /* ignore */
  }
}
